import React, { useState, useEffect } from 'react';
import { useUpdate, useNotify, useDataProvider } from 'react-admin';
import { OnDragEndResponder } from '@hello-pangea/dnd';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SharedKanbanBoard, KanbanStage } from '../../../components/shared/KanbanBoard';
import { KanbanCard } from '../../../components/shared/KanbanCard';

interface Stage extends KanbanStage {
  boardId: string;
  sortOrder: number;
}

export interface OpportunityContext {
  id: string;
  name: string;
  value: number;
  probability: number;
  stageId?: string;
  sortOrder: number;
  boardOrder?: number; // For backward compatibility
  expectedCloseDate?: string;
  customer?: {
    id: string;
    displayName: string;
  };
  lead?: {
    id: string;
    displayName: string;
  };
  assignedUser?: {
    id: number;
    name: string;
  };
}

export type OpportunitiesByStage = Record<string, OpportunityContext[]>;

const calculateNewSortOrder = (
  opportunities: OpportunityContext[],
  destinationIndex: number
): number => {
  const INITIAL_GAP = 1000;
  const sortedOpportunities = [...opportunities].sort((a, b) => {
    // Use sortOrder if available, fall back to boardOrder
    const aOrder = a.sortOrder ?? a.boardOrder ?? 0;
    const bOrder = b.sortOrder ?? b.boardOrder ?? 0;
    return aOrder - bOrder;
  });

  if (destinationIndex === 0) {
    return (sortedOpportunities[0]?.sortOrder ?? INITIAL_GAP) / 2;
  }

  if (destinationIndex >= sortedOpportunities.length) {
    return (sortedOpportunities[sortedOpportunities.length - 1]?.sortOrder ?? 0) + INITIAL_GAP;
  }

  const prevOrder = sortedOpportunities[destinationIndex - 1].sortOrder ?? 0;
  const nextOrder = sortedOpportunities[destinationIndex].sortOrder ?? prevOrder + INITIAL_GAP;
  return (prevOrder + nextOrder) / 2;
};

interface OpportunityBoardContentProps {
  boardId?: string;
  opportunities?: OpportunityContext[];
}

export const OpportunityBoardContent: React.FC<OpportunityBoardContentProps> = ({ 
  boardId, 
  opportunities = [] 
}) => {
  const navigate = useNavigate();
  const [opportunitiesByStage, setOpportunitiesByStage] = useState<OpportunitiesByStage>({});
  const [stages, setStages] = useState<Stage[]>([]);
  const [loadingStages, setLoadingStages] = useState(true);
  const [update] = useUpdate();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  // Load stages for the board
  useEffect(() => {
    const loadStages = async () => {
      if (!boardId) {
        setLoadingStages(false);
        return;
      }

      try {
        // Fetch the board with its stages
        const { data: board } = await dataProvider.getOne('boards', { id: boardId });

        if (board.stages) {
          // Sort stages by sortOrder
          const sortedStages = [...board.stages].sort((a, b) => a.sortOrder - b.sortOrder);
          setStages(sortedStages);
        } else {
          setStages([]);
        }
      } catch (error) {
        notify('Error loading board stages', { type: 'error' });
      } finally {
        setLoadingStages(false);
      }
    };

    loadStages();
  }, [boardId, dataProvider, notify]);

  // Group opportunities by stage
  useEffect(() => {
    const grouped = opportunities.reduce((acc, opportunity) => {
      // Use stageId to group opportunities
      const stageId = opportunity.stageId;

      if (stageId) {
        if (!acc[stageId]) {
          acc[stageId] = [];
        }
        acc[stageId].push(opportunity);
      }
      return acc;
    }, {} as OpportunitiesByStage);

    // Sort opportunities within each stage by sortOrder (or boardOrder for backward compatibility)
    Object.keys(grouped).forEach(stage => {
      grouped[stage].sort((a, b) => {
        const aOrder = a.sortOrder ?? a.boardOrder ?? 0;
        const bOrder = b.sortOrder ?? b.boardOrder ?? 0;
        return aOrder - bOrder;
      });
    });

    setOpportunitiesByStage(grouped);
  }, [opportunities, stages]);

  const onDragEnd: OnDragEndResponder = async result => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const sourceStage = source.droppableId;
    const destinationStage = destination.droppableId;
    const opportunityId = draggableId;

    // Find the opportunity being moved
    const sourceOpportunity = opportunitiesByStage[sourceStage]?.find(
      opp => opp.id === opportunityId
    );
    if (!sourceOpportunity) {
      return;
    }

    // Calculate new sort order
    const destinationOpportunities = opportunitiesByStage[destinationStage] || [];
    const newSortOrder = calculateNewSortOrder(destinationOpportunities, destination.index);

    // Update local state optimistically
    const newOpportunitiesByStage = { ...opportunitiesByStage };

    // Remove from source
    newOpportunitiesByStage[sourceStage] = newOpportunitiesByStage[sourceStage].filter(
      opp => opp.id !== opportunityId
    );

    // Add to destination
    if (!newOpportunitiesByStage[destinationStage]) {
      newOpportunitiesByStage[destinationStage] = [];
    }

    const updatedOpportunity = {
      ...sourceOpportunity,
      stageId: destinationStage,
      sortOrder: newSortOrder,
    };

    newOpportunitiesByStage[destinationStage].splice(destination.index, 0, updatedOpportunity);

    setOpportunitiesByStage(newOpportunitiesByStage);

    try {
      // Update via API using the correct field names
      await update('opportunities', {
        id: opportunityId,
        data: {
          stageId: destinationStage,
          sortOrder: newSortOrder,
        },
        previousData: sourceOpportunity,
      });

      notify('Opportunity moved successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to move opportunity', { type: 'error' });

      // Revert optimistic update
      setOpportunitiesByStage(opportunitiesByStage);
    }
  };

  // Calculate totals per stage
  const stageTotals = stages.reduce((acc, stage) => {
    const stageOpportunities = opportunitiesByStage[stage.id] || [];
    acc[stage.id] = stageOpportunities.reduce((sum, opp) => sum + (opp.value || 0), 0);
    return acc;
  }, {} as Record<string, number>);

  if (loadingStages) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Loading stages...</Typography>
      </Box>
    );
  }

  if (stages.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No stages configured for this board
        </Typography>
      </Box>
    );
  }

  const handleOpportunityClick = (opportunity: OpportunityContext) => {
    navigate(`/opportunities/${opportunity.id}/show`);
  };

  const renderOpportunityCard = (opportunity: OpportunityContext, index: number) => (
    <KanbanCard
      key={opportunity.id}
      item={{
        ...opportunity,
        title: opportunity.name,
        dueDate: opportunity.expectedCloseDate,
      }}
      index={index}
      onClick={handleOpportunityClick}
      showValue={true}
      showProbability={true}
      showDueDate={true}
      showCustomer={true}
      showAssignedUser={true}
    />
  );

  return (
    <SharedKanbanBoard
      stages={stages}
      itemsByStage={opportunitiesByStage}
      stageTotals={stageTotals}
      onDragEnd={onDragEnd}
      renderCard={renderOpportunityCard}
      itemCountLabel="opportunities"
      emptyStateMessage="No opportunities in this stage"
      emptyStateSubtext="Drag opportunities here"
      loading={loadingStages}
    />
  );
};
